import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useCallback } from "react"
import {
	showAccessDeniedPopup,
	showSocialMatchFeedbackPopup,
} from "@/entities/bottom-popup"
import { showNotification } from "@/entities/notification"
import {
	type SocialMatchRequestReason,
	type SocialMatchRequestStatus,
	type SocialMatchStatus,
	socialMatchKeys,
	socialMatchStatusQueryOptions,
	startSocialMatch,
	submitSocialMatch,
	useSocialMatchStatus,
} from "@/entities/social-match"
import { showMonsterPromo } from "@/entities/subscription"
import {
	type AccessDenial,
	type Intent,
	useCurrentUser,
	useIsPremium,
} from "@/entities/user"
import * as m from "@/paraglide/messages"
import { AccessDeniedError } from "@/shared/api/errors"
import { captureError } from "@/shared/lib/diagnostics"
import { showSystemPopup } from "@/shared/lib/platform"
import {
	clearSocialMatchRequestDraft,
	type SocialMatchRequestDraft,
} from "./request-draft"
import { collectSocialMatchRequestReasons } from "./request-reasons"

function okPopup(message: string): void {
	showSystemPopup({ message, buttons: [{ id: "ok", text: "OK", type: "ok" }] })
}

const SOCIAL_INTENT: Intent = { action: "social_match_search", params: {} }

// Луни-диалог не пройден: показываем прогресс «сколько ещё пообщаться»
// (зеркало features/easy-match — тот же эндпоинт отдаёт luni_dialogue_progress).
function showNotReadyPopup(completed: number, required: number): void {
	okPopup(m.socialMatchNotReady({ completed, required }))
}

function showSearchDeniedPopup(code: AccessDenial["code"]): void {
	showAccessDeniedPopup(
		{ code, permission: "social_match_search" },
		SOCIAL_INTENT,
	)
}

// Замок у бесплатного юзера: попыток не осталось.
function isSocialMatchLocked(
	premium: boolean,
	status: SocialMatchStatus,
): boolean {
	if (premium) return false

	return (status.social_match.attempts ?? 0) === 0
}

// Замок бесплатного юзера считается по двум полям статуса: `social_match.attempts`
// (остаток бесплатных попыток) и `social_match.pending` (запущенный, но ещё не
// удовлетворённый поиск). Бэк обнуляет `attempts` вместе с исчерпанием квоты,
// поэтому `pending` — единственный признак, отличающий идущий поиск от закрытого.
//
//   attempts | pending | состояние | тап
//   ---------+---------+-----------+---------------------------------
//      1     |    0    | доступен  | вход во флоу
//      0     |    1    | идёт      | попап «поиск уже запущен»
//      0     |    0    | замок     | пейволл
//
// У премиума `attempts = null`, замок не наступает никогда.
export function useSocialMatch() {
	const user = useCurrentUser()
	const premium = useIsPremium()
	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const statusQuery = useSocialMatchStatus(user.id)

	const data = statusQuery.data

	// Поиск запущен, но ещё не удовлетворён. Пока он идёт, кнопка не под замком:
	// тап показывает попап «уже запущен», а не ведёт на пейволл. Замок вернётся,
	// когда бэк удовлетворит мэтч и pending станет 0.
	const searchPending = (data?.social_match.pending ?? 0) > 0

	const locked =
		data !== undefined && isSocialMatchLocked(premium, data) && !searchPending

	// Гейт «сначала напиши найденному другу» на этом пути разруливает /start:
	// чёрный попап с карточкой друга он присылает по WS (то же событие, что у
	// easy-match), нам остаётся увести на /home, где живёт NotificationOverlay.
	const blockedNoMessage = useCallback((): void => {
		navigate({ to: "/home" })
	}, [navigate])

	const blockedPending = useCallback((): void => {
		showNotification({
			type: "social-match-pending",
			placement: "home",
			origin: "local",
		})
		navigate({ to: "/home" })
	}, [navigate])

	const applySearchStatus = (
		status: SocialMatchRequestStatus,
		onOk: () => void,
	): void => {
		switch (status) {
			case "search_blocked_no_message":
				blockedNoMessage()
				return
			case "search_blocked_pending":
				blockedPending()
				return
			case "quota_used":
				showMonsterPromo()
				return
			case "not_ready":
				queryClient
					.query({ ...socialMatchStatusQueryOptions(user.id), staleTime: 0 })
					.then((status) => {
						showNotReadyPopup(
							status.luni_dialogue_progress.completed,
							status.luni_dialogue_progress.required,
						)
					})
					.catch((error: unknown) => {
						captureError(error, { scope: "socialMatch.notReady" })
						okPopup(m.searchErrorGeneric())
					})
				return
			case "age_required":
			case "age_mismatch":
			case "trust_gate":
				showSearchDeniedPopup(status)
				return
			case "ok":
				onOk()
		}
	}

	const startMutation = useMutation({
		mutationFn: () => startSocialMatch(user.id),
		onSuccess: (res) => {
			applySearchStatus(res.status, () =>
				navigate({ to: "/social-match/request" }),
			)
		},
		onError: (error) => {
			if (error instanceof AccessDeniedError) return
			captureError(error, { scope: "socialMatch.start" })
			okPopup(m.searchErrorGeneric())
		},
	})

	const checkKey = socialMatchKeys.searchCheck(user.id)

	// A mutation rather than a plain read, so every caller sees the check in flight
	// and a second tap is ignored.
	const checkMutation = useMutation({
		mutationKey: checkKey,
		mutationFn: () =>
			queryClient.query({
				...socialMatchStatusQueryOptions(user.id),
				staleTime: 0,
			}),
	})

	const checkSearch = useCallback(
		(onAllowed: () => void, withPaywall: boolean): void => {
			if (queryClient.isMutating({ mutationKey: checkKey }) > 0) return

			checkMutation
				.mutateAsync()
				.then((status) => {
					if (status.gate) {
						const reason = status.gate.reason
						if (reason === "not_ready") {
							showNotReadyPopup(
								status.luni_dialogue_progress.completed,
								status.luni_dialogue_progress.required,
							)
							return
						}
						showSearchDeniedPopup(reason)
						return
					}

					// Paywall outranks the pending / no-message / feedback gates.
					if (
						withPaywall &&
						status.social_match.pending === 0 &&
						isSocialMatchLocked(premium, status)
					) {
						showMonsterPromo()
						return
					}

					if (status.social_match.pending > 0) {
						blockedPending()
						return
					}

					if (status.blocked_no_message) {
						showNotification({
							type: "search-blocked-no-message",
							placement: "home",
							friend: status.blocked_no_message.friend,
							origin: "local",
						})
						navigate({ to: "/home" })
						return
					}

					if (status.pending_feedback) {
						showSocialMatchFeedbackPopup(status.pending_feedback, onAllowed)
						return
					}

					onAllowed()
				})
				.catch((error: unknown) => {
					if (error instanceof AccessDeniedError) return
					captureError(error, { scope: "socialMatch.gate" })
					// Offline: trust the cached lock; /start re-checks access after purchase.
					if (withPaywall && locked) {
						showMonsterPromo()
						return
					}
					okPopup(m.searchErrorGeneric())
				})
		},
		[
			blockedPending,
			locked,
			navigate,
			premium,
			queryClient,
			checkKey,
			checkMutation,
		],
	)

	const startSearch = useCallback((): void => {
		if (startMutation.isPending) return
		checkSearch(() => startMutation.mutate(), true)
	}, [checkSearch, startMutation])

	const openSocialMatch = useCallback((): void => {
		checkSearch(() => navigate({ to: "/social-match" }), false)
	}, [checkSearch, navigate])

	// Между /start и отправкой текста друг мог найтись — бэк повторяет те же гейты.
	const submitMutation = useMutation({
		mutationFn: (draft: SocialMatchRequestDraft) => {
			// Спрятанный блок в запрос не идёт, хотя тексты в черновике живы.
			let reason: SocialMatchRequestReason | null = null
			if (draft.requestReasonsOpen) {
				reason = collectSocialMatchRequestReasons(draft.requestReasons)
			}
			return submitSocialMatch(user.id, {
				request: draft.requestText.trim(),
				reason,
			})
		},
		onSuccess: (res) => {
			queryClient.invalidateQueries({
				queryKey: socialMatchKeys.status(user.id),
			})
			applySearchStatus(res.status, () => {
				clearSocialMatchRequestDraft()
				navigate({ to: "/social-match/submitted" })
			})
		},
		onError: (error) => {
			if (error instanceof AccessDeniedError) return
			captureError(error, { scope: "socialMatch.submit" })
			okPopup(m.socialMatchSubmitError())
		},
	})

	const submit = useCallback(
		(draft: SocialMatchRequestDraft): void => {
			if (submitMutation.isPending) return
			submitMutation.mutate(draft)
		},
		[submitMutation],
	)

	return {
		locked,
		premium,
		attempts: data?.social_match.attempts ?? 0,
		isSubmitting: submitMutation.isPending,
		openSocialMatch,
		startSearch,
		submit,
	}
}
